import Button, { ButtonVariants } from '@gym-atoms/Button/Button';
import Loader from '@gym-atoms/Loader/Loader';
import ActivityFeed from '@gym-molecules/ActivityFeed/ActivityFeed';
import ActivityFeedFilters from '@gym-molecules/ActivityFeedFilters/ActivityFeedFilters';
import EmptyState from '@gym-molecules/EmptyState/EmptyState';
import { Activity } from '@gym-particles/types/ActivityFeed';
import { fetchAccessPoints } from '@gym-redux/slices/accessPointsSlice';
import { useAppDispatch, useAppSelector } from '@gym-redux/store';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const accessLogPageSize = 15;

enum AccessEventFilter {
  ALL = 'all',
  GRANTED = 'GymKeyAccessGranted',
  DENIED = 'GymKeyAccessDenied'
}

const isAccessEvent = (activity: Activity) =>
  activity.eventDescription === AccessEventFilter.GRANTED ||
  activity.eventDescription === AccessEventFilter.DENIED;

const parseDoorIds = (value: unknown): number[] =>
  Array.isArray(value)
    ? value.filter((doorId): doorId is number => typeof doorId === 'number')
    : [];

type CsvHeaders = {
  date: string;
  time: string;
  result: string;
  firstName: string;
  lastName: string;
  email: string;
  door: string;
  location: string;
};

type CsvResults = {
  granted: string;
  denied: string;
};

const escapeCsvValue = (value: string | number) => {
  const text = String(value);
  const safeText = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;

  return `"${safeText.replace(/"/g, '""')}"`;
};

const toCsv = (activities: Activity[], headers: CsvHeaders, results: CsvResults) => {
  const headerRow = Object.values(headers).map(escapeCsvValue).join(',');
  const activityRows = activities.map((activity) =>
    [
      activity.activityDate,
      activity.createdTime,
      activity.eventDescription === AccessEventFilter.GRANTED ? results.granted : results.denied,
      activity.firstName,
      activity.lastName,
      activity.memberEmail,
      activity.locationAccessPointName || '',
      activity.locationName
    ]
      .map(escapeCsvValue)
      .join(',')
  );

  return [headerRow, ...activityRows].join('\r\n');
};

const downloadCsv = (csv: string, fileName: string) => {
  const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const filterAccessEvents = (
  activities: Activity[],
  eventFilter: AccessEventFilter,
  selectedDoorIds: number[],
  availableDoorIds: number[]
) => {
  const allDoorsSelected =
    availableDoorIds.length > 0 && selectedDoorIds.length === availableDoorIds.length;
  const shouldFilterByDoor = selectedDoorIds.length > 0 && !allDoorsSelected;

  return activities.filter(
    (activity) =>
      isAccessEvent(activity) &&
      (eventFilter === AccessEventFilter.ALL || activity.eventDescription === eventFilter) &&
      (!shouldFilterByDoor ||
        (activity.locationAccessPointId !== undefined &&
          selectedDoorIds.includes(activity.locationAccessPointId)))
  );
};

const AccessLog = ({
  gymId,
  activities,
  isLoading,
  totalActivityCount,
  offset,
  startingDate,
  endingDate,
  onOffsetChange,
  onStartingDateChange,
  onEndingDateChange,
  onRefresh,
  countChange,
  isCountChanged,
  showDoorFilter = true
}: AccessLogProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [selectedDoorIds, setSelectedDoorIds] = useState<number[]>([]);
  const [eventFilter, setEventFilter] = useState<AccessEventFilter>(AccessEventFilter.ALL);
  const accessPoints = useAppSelector(
    (state) => state.accessPoint.items.find((item) => item.siteLocationId === gymId)?.items || []
  );
  const doors = useMemo(
    () => accessPoints.filter((accessPoint) => accessPoint.accessPointType === 'door'),
    [accessPoints]
  );
  const doorIds = useMemo(() => doors.map((door) => door.id), [doors]);
  const doorOptions = useMemo(
    () => doors.map((door) => ({ label: door.doorName, value: door.id })),
    [doors]
  );
  const visibleActivities = useMemo(
    () => filterAccessEvents(activities, eventFilter, selectedDoorIds, doorIds),
    [activities, doorIds, eventFilter, selectedDoorIds]
  );

  useEffect(() => {
    setSelectedDoorIds([]);

    if (showDoorFilter) {
      dispatch(fetchAccessPoints({ siteLocationId: gymId }));
    }
  }, [dispatch, gymId, showDoorFilter]);

  const updateEventFilter = (nextFilter: AccessEventFilter) => {
    onOffsetChange(0);
    setEventFilter(nextFilter);
  };

  const exportVisibleActivities = () => {
    const csv = toCsv(
      visibleActivities,
      {
        date: t('GYM_DASHBOARD.CSV_HEADERS.DATE'),
        time: t('GYM_DASHBOARD.CSV_HEADERS.TIME'),
        result: t('GYM_DASHBOARD.CSV_HEADERS.RESULT'),
        firstName: t('GYM_DASHBOARD.CSV_HEADERS.FIRST_NAME'),
        lastName: t('GYM_DASHBOARD.CSV_HEADERS.LAST_NAME'),
        email: t('GYM_DASHBOARD.CSV_HEADERS.EMAIL'),
        door: t('GYM_DASHBOARD.CSV_HEADERS.DOOR'),
        location: t('GYM_DASHBOARD.CSV_HEADERS.LOCATION')
      },
      {
        granted: t('GYM_DASHBOARD.CSV_RESULTS.GRANTED'),
        denied: t('GYM_DASHBOARD.CSV_RESULTS.DENIED')
      }
    );
    const dateFrom = dayjs(startingDate).format('YYYY-MM-DD');
    const dateTo = dayjs(endingDate).format('YYYY-MM-DD');

    downloadCsv(csv, `access-events-${dateFrom}-${dateTo}.csv`);
  };

  return isLoading ? (
    <Loader shape="activity" />
  ) : (
    <>
      <ActivityFeedFilters
        allActivity={eventFilter === AccessEventFilter.ALL}
        errorActivity={eventFilter === AccessEventFilter.DENIED}
        accessActivity={eventFilter === AccessEventFilter.GRANTED}
        dateFrom={startingDate}
        dateTo={endingDate}
        allActivityOnChange={() => updateEventFilter(AccessEventFilter.ALL)}
        errorActivityOnChange={() => updateEventFilter(AccessEventFilter.DENIED)}
        accessActivityOnChange={() => updateEventFilter(AccessEventFilter.GRANTED)}
        dateFromOnChange={(event) => {
          onOffsetChange(0);
          onStartingDateChange(event.target.value);
        }}
        dateToOnChange={(event) => {
          onOffsetChange(0);
          onEndingDateChange(event.target.value);
        }}
        dropdownOptions={doorOptions}
        selectedValue={selectedDoorIds}
        dropdownOnChange={(event) => {
          onOffsetChange(0);
          setSelectedDoorIds(parseDoorIds(event.value));
        }}
        dropdownPlaceholder={t('GYM_DASHBOARD.ALL_DOORS')}
        dropdownChips={true}
        dropdownVisible={showDoorFilter}
      />
      <div className="d-flex justify-content-end mb10">
        <Button
          label={t('GYM_DASHBOARD.EXPORT_CSV')}
          icon="pi-download"
          iconPos="left"
          disabled={visibleActivities.length === 0}
          onClick={exportVisibleActivities}
        />
      </div>
      <div className="activty-feed">
        <ActivityFeed
          activities={visibleActivities}
          stage="siteLocation"
          refreshCallBack={onRefresh}
          countChange={countChange}
          isCountChanged={isCountChanged}
        />
      </div>
      <div className="text-center">
        {visibleActivities.length === 0 && (
          <EmptyState textItems={[t('GYM_DASHBOARD_PAGE.EMPTY_STATE_TEXT')]} />
        )}
        {totalActivityCount > 0 && (
          <div className="d-flex button-view-all justify-content-center">
            <div className="mr15">
              <Button
                data-cy="dashboard-activity-previous-btn"
                variant={ButtonVariants.textonlyslim}
                label={t('GYM_DASHBOARD.VIEW_PREVIOUS_ACTIVITY')}
                icon="pi-angle-left"
                iconPos="left"
                disabled={offset === 0}
                onClick={() => onOffsetChange(offset - accessLogPageSize)}
              />
            </div>
            <Button
              data-cy="dashboard-activity-next-btn"
              variant={ButtonVariants.textonlyslim}
              label={t('GYM_DASHBOARD.VIEW_NEXT_ACTIVITY')}
              icon="pi-angle-right"
              iconPos="right"
              disabled={offset + accessLogPageSize >= totalActivityCount}
              onClick={() => onOffsetChange(offset + accessLogPageSize)}
            />
          </div>
        )}
      </div>
    </>
  );
};

export interface AccessLogProps {
  gymId: number;
  activities: Activity[];
  isLoading: boolean;
  totalActivityCount: number;
  offset: number;
  startingDate: Date;
  endingDate: Date;
  onOffsetChange: (offset: number) => void;
  onStartingDateChange: (date: Date) => void;
  onEndingDateChange: (date: Date) => void;
  onRefresh: () => void;
  countChange?: (value: boolean) => void;
  isCountChanged?: boolean;
  showDoorFilter?: boolean;
}

export default AccessLog;
